<template>
  <div>
    <v-container fluid>
      <v-skeleton-loader
        :loading="!flagNav"
        transition="scale-transition"
        max-height="64"
        type="table-heading"
      >
        <v-toolbar color="primary" class="header-title text-white">
          <v-toolbar-title class="title">Tax Configuration</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <v-card class="card2">
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" md="6">
              <div class="text-subtitle-1 font-weight-medium">
                <v-icon class="mr-1">mdi-file-cog-outline</v-icon>
                Tax information
              </div>
              <p class="text-caption text-grey mt-1 d-flex align-center flex-wrap ga-2">
                <span>{{ taxYear }} / {{ versionCode }}</span>
                <v-chip
                  v-if="versionActive"
                  data-testid="cfg-active-chip"
                  color="success"
                  variant="tonal"
                  size="x-small"
                  prepend-icon="mdi-check-circle"
                >
                  Active
                </v-chip>
              </p>
            </v-col>

            <v-col cols="12" md="6" class="d-flex flex-wrap align-center justify-end ga-2">
              <v-chip
                v-if="dirty"
                data-testid="unsaved-chip"
                color="warning"
                variant="tonal"
                size="small"
                prepend-icon="mdi-content-save-alert"
              >
                Unsaved changes
              </v-chip>
              <v-btn
                variant="outlined"
                color="primary"
                size="large"
                rounded="lg"
                prepend-icon="mdi-arrow-left"
                @click="goBack"
              >
                Back
              </v-btn>
              <v-btn
                data-testid="save-rules-btn"
                color="primary"
                variant="flat"
                size="large"
                rounded="lg"
                prepend-icon="mdi-content-save"
                :loading="saving || reconciling"
                :disabled="saving || reconciling || loading || versionId == null"
                @click="saveAll"
              >
                SAVE
                <v-tooltip activator="parent" location="top">Save all rules for this version</v-tooltip>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- 🆕 R4 — LIVE guard: persistent banner while editing the active version. -->
        <v-card-text v-if="versionActive" class="pt-0">
          <v-alert
            data-testid="cfg-live-banner"
            type="warning"
            variant="tonal"
            density="comfortable"
            icon="mdi-alert"
          >
            เวอร์ชันนี้กำลังใช้งานจริง (LIVE) — การบันทึกจะมีผลกับระบบที่ใช้งานอยู่
          </v-alert>
        </v-card-text>

        <!-- Read-only reference: the personal-info fields the tax engine knows about.
             View-only — these are supplied by the user profile (e.g. DOPA) / form context
             and managed via Tax Question, never added or edited here. -->
        <v-card-text class="pt-0">
          <v-expansion-panels variant="accordion" class="pi-catalog">
            <v-expansion-panel data-testid="personal-info-catalog">
              <v-expansion-panel-title>
                <v-icon class="mr-2" color="primary">mdi-account-details-outline</v-icon>
                <span class="font-weight-medium">ข้อมูลส่วนตัว (Personal Info)</span>
                <v-chip
                  v-if="!personalInfoLoading && !personalInfoError"
                  class="ml-2"
                  size="small"
                  variant="tonal"
                  color="primary"
                >
                  {{ personalInfoFields.length }} ฟิลด์
                </v-chip>
                <v-chip
                  class="ml-2"
                  size="small"
                  variant="tonal"
                  color="grey"
                  prepend-icon="mdi-eye-outline"
                >
                  ดูอย่างเดียว
                </v-chip>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div
                  v-if="personalInfoLoading"
                  class="d-flex align-center ga-2 py-2 text-caption text-grey"
                >
                  <v-progress-circular indeterminate size="18" width="2" color="primary" />
                  กำลังโหลดรายการฟิลด์...
                </div>
                <v-alert
                  v-else-if="personalInfoError"
                  type="warning"
                  variant="tonal"
                  density="compact"
                >
                  {{ personalInfoError }}
                </v-alert>
                <div v-else-if="!personalInfoFields.length" class="text-caption text-grey py-2">
                  ไม่มีฟิลด์ข้อมูลส่วนตัว
                </div>
                <template v-else>
                  <v-table density="comfortable" class="pi-catalog-table">
                    <thead>
                      <tr>
                        <th class="text-left">ฟิลด์</th>
                        <th class="text-left">คำอธิบาย</th>
                        <th class="text-left">ชนิดข้อมูล</th>
                        <th class="text-left">แหล่งข้อมูล</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="field in personalInfoFields" :key="field.name">
                        <td><code class="pi-field-key">{{ field.name }}</code></td>
                        <td>{{ field.description }}</td>
                        <td>
                          <v-chip
                            size="x-small"
                            variant="tonal"
                            :color="field.dataType === 'number' ? 'info' : 'secondary'"
                          >
                            {{ field.dataType }}
                          </v-chip>
                        </td>
                        <td class="text-caption text-grey">{{ field.source }}</td>
                      </tr>
                    </tbody>
                  </v-table>
                  <p class="text-caption text-grey mt-2 mb-0">
                    ฟิลด์เหล่านี้ระบบรับจากโปรไฟล์ผู้ใช้ (เช่น DOPA) หรือบริบทของฟอร์ม และใช้ในเงื่อนไข/สูตรคำนวณภาษี — ไม่สามารถเพิ่มหรือแก้ไขได้ที่นี่
                  </p>
                </template>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>

        <v-card-text>
          <v-row align="center" class="mb-2">
            <v-col cols="12" md="6">
              <v-card variant="outlined" class="pa-2 search-panel">
                <v-row no-gutters>
                  <v-col cols="12" class="pa-2">
                    <v-text-field
                      data-testid="rule-search-input"
                      v-model="search"
                      label="Search tax rules"
                      density="comfortable"
                      variant="outlined"
                      prepend-inner-icon="mdi-magnify"
                      hide-details
                      clearable
                      color="primary"
                    />
                  </v-col>
                </v-row>
              </v-card>
            </v-col>

            <v-col cols="12" md="6" class="d-flex flex-wrap justify-end ga-2">
              <v-btn
                data-testid="open-tax-category-dialog-btn"
                color="primary"
                class="control-btn"
                variant="outlined"
                size="large"
                prepend-icon="mdi-tag-multiple-outline"
                rounded="lg"
                @click="openTaxCategoryDialog"
              >
                TAX CATEGORY
                <v-tooltip activator="parent" location="top">Manage tax categories</v-tooltip>
              </v-btn>
              <v-btn
                data-testid="add-rule-btn"
                color="primary"
                class="control-btn create-btn"
                variant="flat"
                size="large"
                prepend-icon="mdi-plus-circle"
                rounded="lg"
                @click="openAddDialog"
              >
                ADD
                <v-tooltip activator="parent" location="top">Add new tax rule</v-tooltip>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>

        <v-data-table
          data-testid="tax-rules-table"
          :headers="headers"
          :items="rules"
          class="elevation-1 my-data-table"
          density="comfortable"
          :items-per-page="-1"
          fixed-header
          hide-default-footer
          hide-default-body
        >
          <template v-slot:colgroup>
            <col
              v-for="header in headers"
              :key="header.key"
              :style="{ width: header.width, minWidth: header.width }"
            />
          </template>

          <template v-slot:tbody>
            <tbody v-if="groupedRules.length" class="v-data-table__tbody">
              <template v-for="group in groupedRules" :key="group.code">
                <tr class="cfg-category-row">
                  <td :colspan="headers.length">
                    <button
                      type="button"
                      class="cfg-category-toggle"
                      :aria-expanded="!isCollapsed(group.code)"
                      @click="toggleCategoryCollapse(group.code)"
                    >
                      <v-icon
                        size="18"
                        :class="['cfg-category-chevron', { 'is-collapsed': isCollapsed(group.code) }]"
                      >
                        mdi-chevron-down
                      </v-icon>
                      <span class="cfg-category-code">{{ group.code }}</span>
                      <span class="cfg-category-label">{{ group.label }}</span>
                      <span class="cfg-category-count">{{ group.rows.length }} rules</span>
                    </button>
                  </td>
                </tr>
                <template v-if="!isCollapsed(group.code)">
                  <tr v-for="row in group.rows" :key="row.rule_code">
                    <td class="text-center">{{ row.no }}</td>
                    <td>
                      <div class="cfg-rule-cell">
                        <strong>{{ row.rule_code }}</strong>
                        <span class="cfg-rule-label">{{ row.label }}</span>
                      </div>
                    </td>
                    <td>{{ row.category }} {{ row.category_label }}</td>
                    <td class="text-center">
                      <div class="action-group">
                        <v-btn
                          variant="text"
                          density="comfortable"
                          icon
                          class="action-btn edit-btn"
                          @click="openEditDialog(row)"
                        >
                          <v-icon size="18">mdi-pencil-outline</v-icon>
                          <v-tooltip activator="parent" location="top">Edit</v-tooltip>
                        </v-btn>
                        <v-btn
                          variant="text"
                          density="comfortable"
                          icon
                          class="action-btn delete-btn"
                          @click="openDeleteDialog(row)"
                        >
                          <v-icon size="18">mdi-delete-outline</v-icon>
                          <v-tooltip activator="parent" location="top">Delete</v-tooltip>
                        </v-btn>
                      </div>
                    </td>
                  </tr>
                </template>
              </template>
            </tbody>
            <tbody v-else class="v-data-table__tbody">
              <tr>
                <td :colspan="headers.length">
                  <div class="no-data-container">
                    <div class="no-data-content">
                      <v-icon size="64" color="grey-lighten-2" class="no-data-icon">
                        mdi-file-document-outline
                      </v-icon>
                      <div class="no-data-text">
                        <h3 class="no-data-title">No Tax Rules Found</h3>
                        <p class="no-data-subtitle">Click "ADD" to create your first tax rule.</p>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </v-card>
    </v-container>

    <TaxConfigDialog
      v-model="dialogOpen"
      :initial-rule="dialogInitial"
      :existing-rules="rules"
      @submit="onDialogSubmit"
    />

    <TaxCategoryDialog
      v-model="taxCategoryDialog"
      @changed="onTaxCategoriesChanged"
    />

    <!-- 🆕 R4 — pre-save "what changed" confirm dialog (+ LIVE acknowledgement). -->
    <v-dialog v-model="confirmSaveDialog" max-width="560px" persistent data-testid="cfg-confirm-save-dialog">
      <v-card>
        <v-card-title class="d-flex align-center pa-4 bg-primary text-white">
          <v-icon class="mr-2">mdi-content-save-check-outline</v-icon>
          ยืนยันการบันทึก
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert
            v-if="versionActive"
            type="warning"
            variant="tonal"
            density="comfortable"
            icon="mdi-alert"
            class="mb-4"
          >
            เวอร์ชันนี้กำลังใช้งานจริง (LIVE) — การบันทึกจะมีผลกับระบบที่ใช้งานอยู่
          </v-alert>

          <v-alert
            v-if="reconcileWarning"
            type="warning"
            variant="tonal"
            density="comfortable"
            icon="mdi-cloud-alert"
            class="mb-4"
            data-testid="cfg-reconcile-warning"
          >
            {{ reconcileWarning }}
          </v-alert>

          <div class="d-flex flex-wrap ga-2 mb-3" data-testid="cfg-diff-summary">
            <v-chip color="success" variant="tonal" size="small" prepend-icon="mdi-plus">
              เพิ่ม {{ saveDiff.added.length }}
            </v-chip>
            <v-chip color="primary" variant="tonal" size="small" prepend-icon="mdi-pencil">
              แก้ {{ saveDiff.edited.length }}
            </v-chip>
            <v-chip color="error" variant="tonal" size="small" prepend-icon="mdi-delete">
              ลบ {{ saveDiff.removed.length }}
            </v-chip>
            <v-chip
              v-if="saveDiff.preserved && saveDiff.preserved.length"
              color="info"
              variant="tonal"
              size="small"
              prepend-icon="mdi-shield-check"
            >
              คงไว้ {{ saveDiff.preserved.length }}
            </v-chip>
          </div>

          <p
            v-if="!saveDiff.added.length && !saveDiff.edited.length && !saveDiff.removed.length && !(saveDiff.preserved && saveDiff.preserved.length)"
            class="text-caption text-medium-emphasis mb-2"
          >
            ไม่พบการเปลี่ยนแปลง — การบันทึกจะเขียนทับชุดกฎด้วยข้อมูลเดิม
          </p>

          <div v-if="saveDiff.added.length" class="mb-2">
            <div class="text-caption font-weight-medium text-success mb-1">เพิ่ม ({{ saveDiff.added.length }})</div>
            <ul class="cfg-diff-list">
              <li v-for="(r, i) in saveDiff.added.slice(0, 8)" :key="'a' + i">{{ diffRuleLabel(r) }}</li>
              <li v-if="saveDiff.added.length > 8" class="text-grey">…และอีก {{ saveDiff.added.length - 8 }} รายการ</li>
            </ul>
          </div>
          <div v-if="saveDiff.edited.length" class="mb-2">
            <div class="text-caption font-weight-medium text-primary mb-1">แก้ไข ({{ saveDiff.edited.length }})</div>
            <ul class="cfg-diff-list">
              <li v-for="(r, i) in saveDiff.edited.slice(0, 8)" :key="'e' + i">{{ diffRuleLabel(r) }}</li>
              <li v-if="saveDiff.edited.length > 8" class="text-grey">…และอีก {{ saveDiff.edited.length - 8 }} รายการ</li>
            </ul>
          </div>
          <div v-if="saveDiff.removed.length" class="mb-2">
            <div class="text-caption font-weight-medium text-error mb-1">ลบ ({{ saveDiff.removed.length }})</div>
            <ul class="cfg-diff-list">
              <li v-for="(r, i) in saveDiff.removed.slice(0, 8)" :key="'r' + i">{{ diffRuleLabel(r) }}</li>
              <li v-if="saveDiff.removed.length > 8" class="text-grey">…และอีก {{ saveDiff.removed.length - 8 }} รายการ</li>
            </ul>
          </div>
          <div v-if="saveDiff.preserved && saveDiff.preserved.length" class="mb-2" data-testid="cfg-preserved-list">
            <div class="text-caption font-weight-medium text-info mb-1">คงไว้ — ถูกเพิ่มจากหน้าตั้งคำถาม ({{ saveDiff.preserved.length }})</div>
            <ul class="cfg-diff-list">
              <li v-for="(r, i) in saveDiff.preserved.slice(0, 8)" :key="'p' + i">{{ diffRuleLabel(r) }}</li>
              <li v-if="saveDiff.preserved.length > 8" class="text-grey">…และอีก {{ saveDiff.preserved.length - 8 }} รายการ</li>
            </ul>
          </div>

          <v-checkbox
            v-if="versionActive"
            v-model="liveConfirmChecked"
            data-testid="cfg-live-ack"
            color="warning"
            density="comfortable"
            hide-details
            label="ฉันเข้าใจว่านี่คือเวอร์ชันที่ใช้งานจริง"
            class="mt-2"
          />
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="outlined" @click="confirmSaveDialog = false">
            <v-icon start>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn
            data-testid="cfg-confirm-save-btn"
            color="primary"
            variant="elevated"
            :disabled="versionActive && !liveConfirmChecked"
            @click="confirmSave"
          >
            <v-icon start>mdi-content-save</v-icon>
            ยืนยันบันทึก
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="500px" persistent data-testid="rule-delete-dialog">
      <v-card>
        <v-card-title class="bg-error text-white">
          <v-icon class="mr-2">mdi-alert-circle</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" variant="tonal" class="mb-4">
            This action cannot be undone.
          </v-alert>
          <p>Are you sure you want to delete the tax rule:</p>
          <p class="font-weight-bold text-primary">
            {{ deleteTarget?.rule_code }} - {{ deleteTarget?.label }}
          </p>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn data-testid="rule-delete-cancel-btn" variant="outlined" @click="deleteDialog = false">
            <v-icon start>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-btn data-testid="rule-delete-confirm-btn" color="error" variant="elevated" @click="confirmDelete">
            <v-icon start>mdi-delete</v-icon>
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" location="top" timeout="3000">
      {{ snackbarMessage }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { taxConfigStore } from '../utils/taxConfigStore';
import { taxConfigApi } from '../services/taxConfigApi';
import { taxCalculateApi } from '../services/taxCalculateApi';
import TaxConfigDialog from '../components/TaxConfigDialog.vue';
import TaxCategoryDialog from '../components/TaxCategoryDialog.vue';

// 🆕 R4 — stable natural key + content signature for the pre-save "what changed"
// summary. ruleID churns on full-replace recreate, so we key on categoryCode +
// ruleCode (both UPPER_SNAKE), NOT id.
function ruleNaturalKey(rule) {
  const category = String(rule.category || '').trim().toUpperCase();
  const code = String(rule.rule_code || '').trim().toUpperCase();
  return `${category}|${code}`;
}

// Content signature — the meaningful rule fields (excludes ids + volatile
// sort_order so a pure reorder does not read as an edit).
function ruleContentSig(rule) {
  return JSON.stringify({
    category: String(rule.category || '').trim().toUpperCase(),
    rule_code: String(rule.rule_code || '').trim().toUpperCase(),
    label: String(rule.label || '').trim(),
    type: rule.type ?? null,
    active: rule.active !== false,
  });
}

// Best-effort classify current vs baseline by natural key: added (new key),
// edited (same key, changed content), removed (baseline key gone).
function diffByNaturalKey(baseline, current, keyFn, sigFn) {
  const baseMap = new Map();
  (baseline || []).forEach((it) => {
    const k = keyFn(it);
    if (!baseMap.has(k)) baseMap.set(k, it);
  });
  const curMap = new Map();
  (current || []).forEach((it) => {
    const k = keyFn(it);
    if (!curMap.has(k)) curMap.set(k, it);
  });
  const added = [];
  const edited = [];
  const removed = [];
  curMap.forEach((it, k) => {
    if (!baseMap.has(k)) added.push(it);
    else if (sigFn(it) !== sigFn(baseMap.get(k))) edited.push(it);
  });
  baseMap.forEach((it, k) => {
    if (!curMap.has(k)) removed.push(it);
  });
  return { added, edited, removed };
}

export default {
  name: 'TaxConfigurationEdit',
  components: { TaxConfigDialog, TaxCategoryDialog },
  data() {
    return {
      flagNav: true,
      today: new Date().toLocaleDateString(),
      search: '',
      collapsedCategories: new Set(),
      versionId: null,
      versionActive: false,
      loading: false,
      saving: false,
      dirty: false,
      // 🆕 R4 — pre-save diff + LIVE guard. `baseline` is a deep clone of the last
      // loaded rule set; the confirm dialog shows added/edited/removed vs it before
      // committing the (unchanged) full-replace bulk save.
      baseline: [],
      confirmSaveDialog: false,
      liveConfirmChecked: false,
      // 🆕 R1 follow-up — reconcile out-of-band rule additions before the full-replace
      // save. `reconciling` drives the SAVE spinner during the pre-save re-fetch;
      // `reconcileWarning` shows when that re-fetch fails; `saveDiff.preserved` lists
      // rules added elsewhere (e.g. an inline-created flag) that the save will keep.
      reconciling: false,
      reconcileWarning: '',
      saveDiff: { added: [], edited: [], removed: [], preserved: [] },
      headers: [
        { title: 'No.', key: 'no', align: 'center', sortable: false, width: '70px' },
        { title: 'Tax Rule', key: 'rule_code', align: 'start', sortable: false, width: '240px' },
        { title: 'Category', key: 'category_label', align: 'start', sortable: false, width: '220px' },
        { title: 'Action', key: 'actions', align: 'center', sortable: false, width: '120px' },
      ],
      dialogOpen: false,
      dialogInitial: null,
      taxCategoryDialog: false,
      deleteDialog: false,
      deleteTarget: null,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      // Read-only Personal Info catalog (GET api/v1/TaxCalculate/variables → personalInfo).
      // Display-only reference so admins can see which personal-info fields the tax engine
      // knows about; there is intentionally no add/edit here (managed via Tax Question / DOPA).
      personalInfoFields: [],
      personalInfoLoading: false,
      personalInfoError: '',
    };
  },
  computed: {
    taxYear() {
      return String(this.$route.params.taxYear || '').toUpperCase();
    },
    versionCode() {
      return String(this.$route.params.versionCode || '').toUpperCase();
    },
    rules() {
      const all = taxConfigStore.getRules(this.taxYear, this.versionCode);
      const keyword = this.search.trim().toLowerCase();
      const filtered = !keyword
        ? all
        : all.filter((rule) =>
          [rule.rule_code, rule.label, rule.category, rule.category_label]
            .filter(Boolean)
            .some((val) => String(val).toLowerCase().includes(keyword)),
        );
      return filtered;
    },
    groupedRules() {
      const groups = new Map();
      this.rules.forEach((rule, idx) => {
        const code = rule.category || '-';
        const label = rule.category_label || '-';
        const group = groups.get(code) ?? { code, label, rows: [] };
        group.rows.push({ ...rule, no: idx + 1 });
        groups.set(code, group);
      });
      return Array.from(groups.values());
    },
  },
  mounted() {
    this.loadConfig();
    this.loadPersonalInfoCatalog();
    window.addEventListener('beforeunload', this.onBeforeUnload);
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
  },
  // 🆕 R4 — unsaved-work nav guard (mirrors TaxQuestionEdit).
  beforeRouteLeave(to, from, next) {
    if (this.dirty && !window.confirm('ละทิ้งการแก้ไขที่ยังไม่บันทึก?')) {
      next(false);
      return;
    }
    next();
  },
  methods: {
    onBeforeUnload(e) {
      if (this.dirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    },
    // Read-only: fetch the static variable catalog and keep its Personal Info group
    // for display. Never blocks the page — a failure just shows an inline note.
    async loadPersonalInfoCatalog() {
      this.personalInfoLoading = true;
      this.personalInfoError = '';
      try {
        const catalog = await taxCalculateApi.getVariables();
        this.personalInfoFields = (catalog && (catalog.personalInfo || catalog.PersonalInfo)) || [];
      } catch (err) {
        console.error('Failed to load personal-info catalog', err);
        this.personalInfoError = 'ไม่สามารถโหลดรายการฟิลด์ข้อมูลส่วนตัวได้';
        this.personalInfoFields = [];
      } finally {
        this.personalInfoLoading = false;
      }
    },
    async loadConfig() {
      this.loading = true;
      try {
        // On a hard refresh the store is empty — hydrate years + versions first.
        if (taxConfigStore.getYears().length === 0) {
          const years = await taxConfigApi.getYears(true);
          taxConfigStore.loadYearsFromApi(years || []);
          const versions = await taxConfigApi.getVersions(null, true);
          taxConfigStore.loadVersionsFromApi(versions || []);
        }

        const version = taxConfigStore.getVersion(this.taxYear, this.versionCode);
        if (!version) {
          this.showError(`${this.taxYear} / ${this.versionCode} not found`);
          this.$router.replace('/TaxConfiguration');
          return;
        }

        this.versionId = version.versionID ?? null;
        // 🆕 R4 — LIVE guard: the loaded version metadata carries isActive.
        this.versionActive = Boolean(version.isActive);
        if (this.versionId != null) {
          const full = await taxConfigApi.getFullConfig(this.versionId, true);
          taxConfigStore.loadFullConfigIntoVersion(this.taxYear, this.versionCode, full);
        }
        this.dirty = false;
        this.snapshotBaseline();
      } catch (err) {
        this.showError(err?.message || 'Failed to load tax rules');
      } finally {
        this.loading = false;
      }
    },
    // 🆕 R4 — snapshot the loaded rule set as the diff baseline (deep clone so the
    // working copy can mutate freely). Called after every load / re-sync.
    snapshotBaseline() {
      const all = taxConfigStore.getRules(this.taxYear, this.versionCode);
      this.baseline = JSON.parse(JSON.stringify(all || []));
    },
    // 🆕 R4 — best-effort "what changed" summary of the working set vs baseline.
    computeSaveDiff() {
      const all = taxConfigStore.getRules(this.taxYear, this.versionCode);
      return diffByNaturalKey(this.baseline, all, ruleNaturalKey, ruleContentSig);
    },
    // 🆕 R4 — a readable one-line label for a diffed rule in the confirm dialog.
    diffRuleLabel(rule) {
      const code = String(rule.rule_code || '').trim() || '(no code)';
      const label = String(rule.label || '').trim();
      return label ? `${code} — ${label}` : code;
    },
    // 🆕 R4 — SAVE now opens a confirm dialog with the change summary + (when LIVE)
    // an explicit acknowledgement, then commits the existing bulk save on confirm.
    // 🆕 R1 follow-up — before showing the summary, reconcile any rule that landed on
    // the server after this editor loaded (out-of-band — most notably a boolean flag
    // created inline from the Tax Question editor via POST .../rules/append). The save
    // is a full-replace built from this editor's in-memory set, which does not know
    // about that flag, so an unguarded save would silently delete it. We merge such
    // rules back so the save preserves them, and surface them in the confirm dialog.
    async saveAll() {
      if (this.versionId == null) {
        this.showError('This version is not persisted yet — cannot save');
        return;
      }
      this.reconcileWarning = '';
      this.reconciling = true;
      try {
        const preserved = await this.reconcileForeignRules();
        this.saveDiff = this.computeSaveDiff();
        // Merged foreign rules aren't in the baseline, so computeSaveDiff would file
        // them under "added" — the admin didn't add them. Move them to their own
        // "preserved" bucket so the summary stays honest.
        const foreignKeys = new Set(preserved.map(ruleNaturalKey));
        this.saveDiff.added = this.saveDiff.added.filter((r) => !foreignKeys.has(ruleNaturalKey(r)));
        this.saveDiff.preserved = preserved;
      } catch (err) {
        // Non-blocking: we could not verify server state. Warn, but let the admin
        // decide — blocking the save entirely would be worse than a visible caveat.
        this.saveDiff = this.computeSaveDiff();
        this.saveDiff.preserved = [];
        this.reconcileWarning =
          'ตรวจสอบ rule ล่าสุดจากเซิร์ฟเวอร์ไม่สำเร็จ — หากเพิ่งสร้าง flag จากหน้าตั้งคำถาม การบันทึกอาจลบ flag นั้นทิ้ง';
      } finally {
        this.reconciling = false;
      }
      this.liveConfirmChecked = false;
      this.confirmSaveDialog = true;
    },
    // 🆕 R1 follow-up — fetch the server's authoritative rule set and merge any rule
    // whose natural key is absent from BOTH the current working set and the load-time
    // baseline (⇒ added elsewhere since this editor opened) into the working set, so
    // the full-replace save keeps it. Returns the merged ("preserved") rules for the
    // confirm dialog. Never overwrites the admin's local edits — a key present locally
    // is skipped, and a baseline key the admin explicitly deleted is not re-added.
    async reconcileForeignRules() {
      const full = await taxConfigApi.getFullConfig(this.versionId, true);
      const serverRules = [];
      ((full && full.categories) || []).forEach((cat) => {
        (cat.rules || []).forEach((dto) => {
          serverRules.push(taxConfigStore.mapRuleDtoToUi(dto, cat.categoryLabel));
        });
      });
      const working = taxConfigStore.getRules(this.taxYear, this.versionCode);
      const localKeys = new Set(working.map(ruleNaturalKey));
      const baselineKeys = new Set((this.baseline || []).map(ruleNaturalKey));
      // ruleCode is the per-version unique key server-side. `upsertRule` matches on
      // rule_code alone, so merging a foreign rule whose code already exists locally
      // (even under a different category) would OVERWRITE the admin's row. The two
      // can't coexist on save anyway (unique ruleCode) — let the admin's explicit
      // rule win: skip the merge and leave it out of the "preserved" list.
      const localCodes = new Set(
        working.map((r) => String(r.rule_code || '').trim().toUpperCase()).filter(Boolean),
      );
      const foreign = serverRules.filter((r) => {
        const k = ruleNaturalKey(r);
        if (localKeys.has(k) || baselineKeys.has(k)) return false;
        const code = String(r.rule_code || '').trim().toUpperCase();
        return !localCodes.has(code);
      });
      foreign.forEach((r) => {
        taxConfigStore.upsertRule(this.taxYear, this.versionCode, r);
        // Fold into the diff baseline as well: these rules already exist on the
        // server, so if the admin cancels and later saves again they must read as
        // pre-existing (not "added"). reconcile is idempotent — a merged key is now
        // in the working set, so a re-run won't flag it as foreign a second time.
        this.baseline.push(JSON.parse(JSON.stringify(r)));
      });
      return foreign;
    },
    async confirmSave() {
      this.confirmSaveDialog = false;
      await this.commitSave();
    },
    async commitSave() {
      if (this.versionId == null) {
        this.showError('This version is not persisted yet — cannot save');
        return;
      }
      this.saving = true;
      try {
        const allRules = taxConfigStore.getRules(this.taxYear, this.versionCode);
        const payload = allRules.map((rule, idx) =>
          taxConfigStore.buildRuleRequest({ ...rule, sort_order: rule.sort_order ?? idx }, this.versionId),
        );
        await taxConfigApi.bulkSaveRules(this.versionId, payload);
        // Re-sync from server (fresh ruleIDs / canonical order).
        const full = await taxConfigApi.getFullConfig(this.versionId, true);
        taxConfigStore.loadFullConfigIntoVersion(this.taxYear, this.versionCode, full);
        this.dirty = false;
        this.snapshotBaseline();
        this.showSuccess('Tax rules saved');
      } catch (err) {
        this.showError(err?.message || 'Failed to save tax rules');
      } finally {
        this.saving = false;
      }
    },
    isCollapsed(code) {
      return this.collapsedCategories.has(code);
    },
    toggleCategoryCollapse(code) {
      const next = new Set(this.collapsedCategories);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      this.collapsedCategories = next;
    },
    goBack() {
      // 🆕 R4 — the beforeRouteLeave guard prompts on unsaved changes; router.push
      // triggers it, so no extra confirm needed here.
      this.$router.push('/TaxConfiguration');
    },
    openAddDialog() {
      this.dialogInitial = null;
      this.dialogOpen = true;
    },
    openTaxCategoryDialog() {
      this.taxCategoryDialog = true;
    },
    openEditDialog(rule) {
      this.dialogInitial = { ...rule };
      this.dialogOpen = true;
    },
    onTaxCategoriesChanged(categories) {
      const labelByCode = new Map(
        (categories || [])
          .map((item) => [
            String(item.categoryCode ?? item.CategoryCode ?? '').trim(),
            String(item.labelTh ?? item.LabelTh ?? '').trim(),
          ])
          .filter(([code]) => code),
      );

      taxConfigStore.getRules(this.taxYear, this.versionCode).forEach((rule) => {
        const label = labelByCode.get(String(rule.category || '').trim());
        if (label) rule.category_label = label;
      });
    },
    onDialogSubmit(rule) {
      const ruleCode = String(rule.rule_code || '').trim();
      if (!ruleCode) return;

      const isEdit = Boolean(this.dialogInitial && this.dialogInitial.rule_code);
      if (!isEdit) {
        const existing = taxConfigStore.getRules(this.taxYear, this.versionCode);
        if (existing.some((row) => row.rule_code === ruleCode)) {
          this.showError(`Rule code ${ruleCode} already exists`);
          return;
        }
      }

      taxConfigStore.upsertRule(this.taxYear, this.versionCode, rule);
      this.dirty = true;
      this.dialogOpen = false;
      this.dialogInitial = null;
      this.showSuccess(isEdit ? 'Tax rule updated — click Save to persist' : 'Tax rule added — click Save to persist');
    },
    openDeleteDialog(rule) {
      this.deleteTarget = rule;
      this.deleteDialog = true;
    },
    confirmDelete() {
      if (!this.deleteTarget) return;
      taxConfigStore.deleteRule(this.taxYear, this.versionCode, this.deleteTarget.rule_code);
      this.dirty = true;
      this.deleteDialog = false;
      this.deleteTarget = null;
      this.showSuccess('Tax rule removed — click Save to persist');
    },
    showSuccess(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'success';
      this.snackbar = true;
    },
    showError(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'error';
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.cfg-rule-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* 🆕 R4 — compact bullet list of changed rules in the pre-save confirm dialog. */
.cfg-diff-list {
  margin: 0;
  padding-left: 18px;
  font-size: 0.85rem;
}

.cfg-rule-label {
  font-size: 0.85rem;
  color: #6b7280;
}

.cfg-category-row td {
  background: rgba(var(--v-theme-primary), 0.04);
  border-top: 1px solid rgba(var(--v-theme-primary), 0.12);
  padding: 0 !important;
}

.cfg-category-row:hover td {
  background: rgba(var(--v-theme-primary), 0.06);
}

.cfg-category-toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 16px;
  background: transparent;
  border: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  color: #1f2937;
}

.cfg-category-chevron {
  transition: transform 0.2s ease;
}

.cfg-category-chevron.is-collapsed {
  transform: rotate(-90deg);
}

.cfg-category-code {
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
}

.cfg-category-label {
  font-weight: 500;
}

.cfg-category-count {
  margin-left: auto;
  font-size: 0.8rem;
  color: #6b7280;
}

.no-data-container {
  padding: 48px 24px;
  text-align: center;
}

.no-data-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.no-data-title {
  font-size: 1.1rem;
  font-weight: 500;
  color: #616161;
}

.no-data-subtitle {
  color: #9e9e9e;
  font-size: 0.875rem;
}

.action-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 110px;
}

.action-btn {
  transition: all 0.2s ease;
}

.action-btn:hover {
  transform: scale(1.1);
}

.delete-btn:hover {
  color: rgb(var(--v-theme-error)) !important;
}

:deep(.my-data-table .v-table__wrapper) {
  max-height: 620px !important;
  overflow: auto !important;
}

:deep(.my-data-table table) {
  table-layout: fixed;
  min-width: 1120px;
  width: 100%;
}

:deep(.my-data-table thead th),
:deep(.my-data-table tbody td) {
  vertical-align: middle;
}

:deep(.my-data-table tbody td) {
  height: 56px;
  padding: 0 16px;
}

/* Read-only Personal Info catalog panel */
.pi-catalog-table {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 6px;
}

.pi-field-key {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.82rem;
  padding: 1px 6px;
  border-radius: 4px;
  background: rgba(var(--v-theme-primary), 0.08);
  color: rgb(var(--v-theme-primary));
}
</style>
